import * as Lambda from '../../services/LambdaApi';
import { Comment, CommentListResponse } from '../types/Comment';

const PAGE_SIZE = 50;
type CommentTargetType = 'requestInstance' | 'serviceInstance' | 'post';

const resolveTarget = ({
    requestInstanceId,
    serviceInstanceId,
    postId,
    targetType,
}: {
    requestInstanceId?: string;
    serviceInstanceId?: string;
    postId?: string;
    targetType?: CommentTargetType;
}) => {
    const hasRequest = Boolean(requestInstanceId);
    const hasService = Boolean(serviceInstanceId);
    const hasPost = Boolean(postId);
    const providedCount = [hasRequest, hasService, hasPost].filter(Boolean).length;

    if (providedCount !== 1) {
        throw new Error('Exactly one of requestInstanceId, serviceInstanceId or postId is required');
    }

    const resolvedTargetType: CommentTargetType = targetType || (hasPost ? 'post' : hasService ? 'serviceInstance' : 'requestInstance');
    if (resolvedTargetType === 'requestInstance' && !hasRequest) {
        throw new Error('requestInstanceId is required when targetType is requestInstance');
    }
    if (resolvedTargetType === 'serviceInstance' && !hasService) {
        throw new Error('serviceInstanceId is required when targetType is serviceInstance');
    }
    if (resolvedTargetType === 'post' && !hasPost) {
        throw new Error('postId is required when targetType is post');
    }

    return {
        targetType: resolvedTargetType,
        requestInstanceId: hasRequest ? String(requestInstanceId) : undefined,
        serviceInstanceId: hasService ? String(serviceInstanceId) : undefined,
        postId: hasPost ? String(postId) : undefined,
    };
};

/**
 * List comments for a request, service instance, or post. Does not pass ApprovalStatus so all
 * approved comments are returned; passes registrantId so the backend can also include the
 * caller's own pending comment (and only their own) — UI shows "Pending" for that item.
 */
const listComments = async ({
    facilityId,
    registrantId,
    requestInstanceId,
    serviceInstanceId,
    postId,
    pageNo = 1,
    pageSize = PAGE_SIZE,
}: {
    facilityId: string;
    registrantId?: string;
    requestInstanceId?: string;
    serviceInstanceId?: string;
    postId?: string;
    pageNo?: number;
    pageSize?: number;
}): Promise<CommentListResponse> => {
    const target = resolveTarget({ requestInstanceId, serviceInstanceId, postId });
    const apiPath = `/s2l/${facilityId}/listComments`;
    const params = {
        ...(target.requestInstanceId ? { requestInstanceId: target.requestInstanceId } : {}),
        ...(target.serviceInstanceId ? { serviceInstanceId: target.serviceInstanceId } : {}),
        ...(target.postId ? { postId: target.postId } : {}),
        ...(registrantId ? { registrantId } : {}),
        pagingSorting: { page_no: pageNo, page_size: pageSize },
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    const content = response?.Result?.Content;
    if (content && typeof content.Result !== 'undefined') {
        return {
            Result: Array.isArray(content.Result) ? content.Result : [],
            TotRecords: typeof content.TotRecords === 'number' ? content.TotRecords : 0,
        };
    }
    if (response?.Result && Array.isArray(response.Result.Result)) {
        return {
            Result: response.Result.Result,
            TotRecords: response.Result.TotRecords ?? response.Result.Result.length,
        };
    }
    return { Result: [], TotRecords: 0 };
};

/**
 * Add a comment as resident. Uses registrantId only (resident-only flow).
 */
const createComment = async ({
    facilityId,
    registrantId,
    requestInstanceId,
    serviceInstanceId,
    postId,
    targetType,
    text,
    imageUrl,
    imageFileName,
}: {
    facilityId: string;
    registrantId: string;
    requestInstanceId?: string;
    serviceInstanceId?: string;
    postId?: string;
    targetType?: CommentTargetType;
    text: string;
    imageUrl?: string;
    imageFileName?: string;
}): Promise<Comment> => {
    const target = resolveTarget({ requestInstanceId, serviceInstanceId, postId, targetType });
    const apiPath = `/s2l/${facilityId}/createComment`;
    const params = {
        text: text.trim().slice(0, 500),
        targetType: target.targetType,
        ...(target.requestInstanceId ? { requestInstanceId: target.requestInstanceId } : {}),
        ...(target.serviceInstanceId ? { serviceInstanceId: target.serviceInstanceId } : {}),
        ...(target.postId ? { postId: target.postId } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(imageFileName ? { imageFileName } : {}),
        registrantId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    const content = response?.Result?.Content;
    if (!content && !response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error creating comment');
    }
    return content ?? response?.Result ?? {};
};

/**
 * Get a presigned upload URL for a comment photo (post comments).
 */
const getCommentUploadUrl = async ({
    facilityId,
    fileName,
}: {
    facilityId: string;
    fileName: string;
}): Promise<{ presignedUrl: string; imageUrl: string; fileName: string }> => {
    const apiPath = `/s2l/${facilityId}/getCommentUploadUrl`;
    const response = await Lambda.Post(apiPath, { fileName }, 'lambda');
    if (!response?.Result?.Content) {
        throw new Error('Error getting comment upload URL');
    }
    return response.Result.Content;
};

/**
 * Update a comment (resident author). Sends registrantId for auth.
 */
const updateComment = async ({
    facilityId,
    registrantId,
    commentId,
    text,
}: {
    facilityId: string;
    registrantId: string;
    commentId: string;
    text: string;
}): Promise<Comment> => {
    const apiPath = `/s2l/${facilityId}/updateComment`;
    const params = {
        _id: commentId,
        text: text.trim().slice(0, 500),
        registrantId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    const content = response?.Result?.Content;
    if (!content && !response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error updating comment');
    }
    return content ?? response?.Result ?? {};
};

/**
 * Delete a comment (resident author). Sends registrantId for auth.
 */
const deleteComment = async ({
    facilityId,
    registrantId,
    commentId,
}: {
    facilityId: string;
    registrantId: string;
    commentId: string;
}): Promise<{ deleted?: boolean; _id?: string; message?: string; Message?: string }> => {
    const apiPath = `/s2l/${facilityId}/deleteComment`;
    const params = { _id: commentId, registrantId };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    const content = response?.Result?.Content;
    if (!response?.Result?.Success && content?.deleted !== true) {
        throw new Error(response?.Result?.Error || 'Error deleting comment');
    }
    return content ?? { deleted: true, _id: commentId };
};

export { listComments, createComment, updateComment, deleteComment, getCommentUploadUrl };
